import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { base44 } from "api/apiClient";
import Layout from "../components/Layout";
import useFinanceData from "../hooks/useFinanceData";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, TrendingUp, Users } from "lucide-react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { Textarea } from "components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "components/ui/alert-dialog";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const CATEGORY_COLORS = {
  Wareneinkauf: "bg-blue-50 text-blue-700",
  Miete: "bg-purple-50 text-purple-700",
  Personal: "bg-green-50 text-green-700",
  "Strom/Gas": "bg-yellow-50 text-yellow-700",
  Versicherungen: "bg-orange-50 text-orange-700",
  Marketing: "bg-pink-50 text-pink-700",
  Reparaturen: "bg-red-50 text-red-700",
  Sonstiges: "bg-slate-100 text-slate-700",
};

const EMPTY_EXPENSE = {
  date: format(new Date(), "yyyy-MM-dd"),
  category: "Wareneinkauf",
  description: "",
  amount: "",
  supplier: "",
  is_recurring: false,
  recurring_period: "Monatlich",
  invoice_number: "",
};

export default function FinancePerformance() {
  const { t, i18n } = useTranslation();
  const finance = useFinanceData();
  const locale = i18n.language === "de" ? de : enUS;
  const queryClient = useQueryClient();

  // Expense CRUD state
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [expenseForm, setExpenseForm] = useState({ ...EMPTY_EXPENSE });
  const [showRecurringOnly, setShowRecurringOnly] = useState(false);

  // Day detail dialog
  const [selectedDay, setSelectedDay] = useState(null);

  const { data: forecastResponse, isLoading: forecastLoading } = useQuery({
    queryKey: ['forecast', 'guests', 7],
    queryFn: () => base44.forecast.getGuests(7),
    initialData: { forecasts: [], meta: {} },
    staleTime: 5 * 60 * 1000,
  });
  const forecastDays = forecastResponse?.forecasts || [];

  const resetExpenseForm = () => {
    setExpenseForm({ ...EMPTY_EXPENSE });
    setEditingExpense(null);
  };

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Expense.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Expense.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Expense.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setDeleteExpenseId(null);
    },
  });

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      date: expense.date || format(new Date(), "yyyy-MM-dd"),
      category: expense.category || "Wareneinkauf",
      description: expense.description || "",
      amount: expense.amount?.toString() || "",
      supplier: expense.supplier || "",
      is_recurring: expense.is_recurring || false,
      recurring_period: expense.recurring_period || "Monatlich",
      invoice_number: expense.invoice_number || "",
    });
    setIsExpenseDialogOpen(true);
  };

  const handleSubmitExpense = (e) => {
    e.preventDefault();
    const data = { ...expenseForm, amount: parseFloat(expenseForm.amount) };
    if (editingExpense) {
      updateMutation.mutate({ id: editingExpense.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleChartClick = (data) => {
    if (data?.activePayload?.[0]) {
      setSelectedDay(data.activePayload[0].payload);
    }
  };

  const monthLabel = format(finance.selectedMonth, "MMMM yyyy", { locale });

  // Filter expenses for table
  const filteredExpenses = showRecurringOnly
    ? finance.expenses.filter((e) => e.is_recurring)
    : finance.expenses;

  const chartData = finance.dailyData.filter((d) => !d.isFuture);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">

        {/* ── Section 1: Month nav + 3 key numbers ── */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={finance.prevMonth}
              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-semibold text-slate-800 min-w-[10rem] text-center capitalize">
              {monthLabel}
            </span>
            <button
              onClick={finance.nextMonth}
              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            {finance.isCurrentMonth && (
              <span className="text-sm text-slate-400 ml-2">
                {finance.openDaysPassed}/{finance.totalOpenDays} {t("revenue.openingDays")}
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div>
              <p className="text-sm text-slate-500 mb-1">{t("finances.revenue")}</p>
              <p className="text-3xl font-bold text-slate-900">
                {finance.revenue.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
              {finance.lastMonthRevenue > 0 && (
                <p className={`text-sm mt-1 ${finance.revenueMoM >= 0 ? "text-green-600" : "text-red-500"}`}>
                  {finance.revenueMoM >= 0 ? "+" : ""}{finance.revenueMoM.toFixed(1)}% vs {t("revenue.previousMonth")}
                </p>
              )}
            </div>

            <div>
              <p className="text-sm text-slate-500 mb-1">{t("finances.expenses")}</p>
              <p className="text-3xl font-bold text-slate-900">
                {finance.totalExpenses.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {finance.guests} {t("posPerformance.total")} {t("dashboard.guests")}
              </p>
            </div>

            <div>
              <p className="text-sm text-slate-500 mb-1">{t("finances.profit")}</p>
              <p className={`text-3xl font-bold ${finance.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                {finance.profit.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {finance.profitMargin.toFixed(1)}% {t("finances.margin")}
              </p>
            </div>
          </div>
        </div>

        {/* ── Section 2: Forecast line ── */}
        {finance.remainingOpenDays > 0 && !finance.isFutureMonth && (
          <p className="text-sm text-slate-500 border-t border-slate-100 pt-4">
            {t("revenue.forecastEndOfMonth")}:&nbsp;
            <span className="font-medium text-slate-700">
              {finance.projectedRevenue.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} {t("finances.revenue")}
            </span>
            &nbsp;&middot;&nbsp;
            <span className={`font-medium ${finance.projectedProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
              {finance.projectedProfit.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} {t("finances.profit")}
            </span>
            &nbsp;
            <span className="text-slate-400">
              ({finance.projectedProfitMargin.toFixed(1)}% {t("finances.margin")})
            </span>
          </p>
        )}

        {/* ── Section 3: 7-day forecast ── */}
        <div className="border border-slate-200 rounded-lg bg-white p-6">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-800">{t("finances.forecastNext7Days", "Forecast — next 7 days")}</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">{t("finances.forecastSubtitle", "Revenue and cost estimate based on expected guest count")}</p>
          {forecastLoading ? (
            <p className="text-sm text-slate-400 py-4 text-center">Loading…</p>
          ) : forecastDays.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">{t("dashboard.noForecastData", "Not enough data for a forecast yet.")}</p>
          ) : (() => {
            const cogsRate = finance.historicalCogsRate;
            const laborRate = finance.historicalLaborRate;
            const rows = forecastDays.map(f => {
              const revenue = f.is_closed ? 0 : (f.revenue || 0);
              const material = revenue * cogsRate;
              const labor = revenue * laborRate;
              const profit = revenue - material - labor;
              return { ...f, revenue, material, labor, profit };
            });
            const totals = rows.reduce(
              (acc, r) => ({ revenue: acc.revenue + r.revenue, material: acc.material + r.material, labor: acc.labor + r.labor, profit: acc.profit + r.profit }),
              { revenue: 0, material: 0, labor: 0, profit: 0 }
            );
            const fmt = (n) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
            const dayShort = { Montag: "Mo", Dienstag: "Di", Mittwoch: "Mi", Donnerstag: "Do", Freitag: "Fr", Samstag: "Sa", Sonntag: "So" };
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs text-slate-400 uppercase tracking-wide">
                      <th className="text-left pb-3 font-medium">Tag</th>
                      <th className="text-right pb-3 font-medium">
                        <span className="flex items-center justify-end gap-1"><Users className="w-3 h-3" /> Gäste</span>
                      </th>
                      <th className="text-right pb-3 font-medium">Umsatz</th>
                      <th className="text-right pb-3 font-medium">Material</th>
                      <th className="text-right pb-3 font-medium">Personal</th>
                      <th className="text-right pb-3 font-medium">Gewinn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((f, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="py-2.5 text-slate-700 font-medium">
                          {dayShort[f.day_name] || f.day_name?.slice(0, 2)}&nbsp;
                          <span className="text-slate-400 font-normal text-xs">{f.date ? f.date.slice(5).replace("-", ".") : ""}</span>
                        </td>
                        <td className="py-2.5 text-right text-slate-600">
                          {f.is_closed ? <span className="text-slate-300 text-xs">Geschlossen</span> : (f.guests || "–")}
                        </td>
                        <td className="py-2.5 text-right text-slate-800 font-medium">{f.is_closed ? "–" : fmt(f.revenue)}</td>
                        <td className="py-2.5 text-right text-slate-500">{f.is_closed ? "–" : fmt(f.material)}</td>
                        <td className="py-2.5 text-right text-slate-500">{f.is_closed ? "–" : fmt(f.labor)}</td>
                        <td className={`py-2.5 text-right font-semibold ${f.profit >= 0 ? "text-green-600" : "text-red-500"}`}>
                          {f.is_closed ? "–" : fmt(f.profit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50 text-sm font-semibold">
                      <td className="py-3 pl-1 text-slate-600">Gesamt</td>
                      <td />
                      <td className="py-3 text-right text-slate-800">{fmt(totals.revenue)}</td>
                      <td className="py-3 text-right text-slate-500">{fmt(totals.material)}</td>
                      <td className="py-3 text-right text-slate-500">{fmt(totals.labor)}</td>
                      <td className={`py-3 text-right ${totals.profit >= 0 ? "text-green-600" : "text-red-500"}`}>{fmt(totals.profit)}</td>
                    </tr>
                  </tfoot>
                </table>
                <p className="text-xs text-slate-400 mt-3">
                  Material {(cogsRate * 100).toFixed(0)}% · Personal {(laborRate * 100).toFixed(0)}% — basierend auf historischen Werten
                </p>
              </div>
            );
          })()}
        </div>

        {/* ── Section 4: Daily chart ── */}
        <div className="border border-slate-200 rounded-lg bg-white p-6">
          <h2 className="text-base font-semibold text-slate-800 mb-1">
            {t("finances.dailyProfitLoss")}
          </h2>
          <p className="text-xs text-slate-400 mb-4">{t("revenue.clickDayForDetails")}</p>

          {chartData.some((d) => d.revenue > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData} onClick={handleChartClick} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: 13 }}
                  formatter={(value) => `€${value.toFixed(2)}`}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#revGrad)"
                  name={t("finances.revenue")}
                  dot={false}
                  activeDot={{ r: 4, cursor: "pointer" }}
                />
                <Area
                  type="monotone"
                  dataKey="costs"
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  fill="none"
                  strokeDasharray="4 3"
                  name={t("finances.costs")}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">
              {t("posPerformance.noData")}
            </div>
          )}
        </div>

        {/* ── Section 4: Expense table ── */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-base font-semibold text-slate-800">{t("finances.expensesOverview")}</h2>
              <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
                <Checkbox
                  checked={showRecurringOnly}
                  onCheckedChange={setShowRecurringOnly}
                  className="h-4 w-4"
                />
                {t("finances.recurring")}
              </label>
            </div>

            <Dialog
              open={isExpenseDialogOpen}
              onOpenChange={(open) => {
                setIsExpenseDialogOpen(open);
                if (!open) resetExpenseForm();
              }}
            >
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-1.5" />
                  {t("finances.addExpense")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingExpense ? t("finances.editExpense") : t("finances.newExpense")}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitExpense} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">{t("finances.date")}</Label>
                      <Input
                        id="date"
                        type="date"
                        value={expenseForm.date}
                        onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">{t("finances.category")}</Label>
                      <Select
                        value={expenseForm.category}
                        onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.keys(CATEGORY_COLORS).map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {t(`finances.categories.${cat}`, cat)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="amount">{t("finances.amount")}</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        value={expenseForm.amount}
                        onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="supplier">{t("finances.supplier")}</Label>
                      <Input
                        id="supplier"
                        value={expenseForm.supplier}
                        onChange={(e) => setExpenseForm({ ...expenseForm, supplier: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">{t("finances.description")}</Label>
                    <Textarea
                      id="description"
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <div className="flex items-center gap-3 py-2">
                    <Checkbox
                      id="is_recurring"
                      checked={expenseForm.is_recurring}
                      onCheckedChange={(checked) => setExpenseForm({ ...expenseForm, is_recurring: checked })}
                    />
                    <Label htmlFor="is_recurring" className="text-sm cursor-pointer">
                      {t("finances.recurring")}
                    </Label>
                    {expenseForm.is_recurring && (
                      <Select
                        value={expenseForm.recurring_period}
                        onValueChange={(v) => setExpenseForm({ ...expenseForm, recurring_period: v })}
                      >
                        <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monatlich">{t("finances.monthly")}</SelectItem>
                          <SelectItem value="Wöchentlich">{t("finances.weekly")}</SelectItem>
                          <SelectItem value="Jährlich">{t("finances.yearly")}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setIsExpenseDialogOpen(false)}>
                      {t("finances.cancel")}
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      {editingExpense ? t("finances.update") : t("finances.save")}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="w-28">{t("finances.date")}</TableHead>
                  <TableHead>{t("finances.category")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("finances.description")}</TableHead>
                  <TableHead className="text-right">{t("finances.amount")}</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                      {t("finances.noExpenses")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredExpenses.map((expense) => (
                    <TableRow key={expense.id} className="group">
                      <TableCell className="text-sm text-slate-600">
                        {format(new Date(expense.date), "dd.MM.yy")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={`text-xs font-normal ${CATEGORY_COLORS[expense.category] || ""}`}>
                          {t(`finances.categories.${expense.category}`, expense.category)}
                        </Badge>
                        {expense.is_recurring && (
                          <Badge variant="outline" className="text-xs ml-1.5">
                            {t(`finances.periods.${expense.recurring_period}`, expense.recurring_period)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-slate-500 max-w-xs truncate">
                        {expense.description || expense.supplier || "\u2014"}
                      </TableCell>
                      <TableCell className="text-right font-medium text-slate-900">
                        {expense.amount?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-slate-400 hover:text-blue-600"
                            onClick={() => handleEditExpense(expense)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-slate-400 hover:text-red-600"
                            onClick={() => setDeleteExpenseId(expense.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* ── Delete confirmation ── */}
        <AlertDialog open={!!deleteExpenseId} onOpenChange={() => setDeleteExpenseId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("finances.deleteExpense")}</AlertDialogTitle>
              <AlertDialogDescription>{t("finances.deleteExpenseDesc")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("finances.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate(deleteExpenseId)}
                className="bg-red-600 hover:bg-red-700"
              >
                {t("finances.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Day detail dialog ── */}
        <Dialog open={!!selectedDay} onOpenChange={(open) => { if (!open) setSelectedDay(null); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            {selectedDay && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-base">
                    {format(new Date(selectedDay.date), "EEEE, d. MMMM yyyy", { locale })}
                  </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-3 gap-4 py-3 border-b border-slate-100">
                  <div>
                    <p className="text-xs text-slate-500">{t("finances.revenue")}</p>
                    <p className="text-lg font-bold text-slate-900">
                      {selectedDay.revenue.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t("finances.costs")}</p>
                    <p className="text-lg font-bold text-slate-900">
                      {selectedDay.costs.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t("finances.profit")}</p>
                    <p className={`text-lg font-bold ${selectedDay.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {selectedDay.profit.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                </div>

                {selectedDay.guests > 0 && (
                  <p className="text-sm text-slate-500">
                    {selectedDay.guests} {t("dashboard.guests")} &middot; {(selectedDay.revenue / selectedDay.guests).toLocaleString("de-DE", { style: "currency", currency: "EUR" })} {t("revenue.avgPerGuest")}
                  </p>
                )}

                {/* Cost breakdown */}
                <div className="space-y-2 py-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("revenue.costOfGoods")} (COGS)</span>
                    <span className="font-medium text-slate-900">
                      {selectedDay.cogs.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("revenue.laborCosts")}</span>
                    <span className="font-medium text-slate-900">
                      {selectedDay.laborCost.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </span>
                  </div>
                  {selectedDay.otherExpenses > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t("revenue.otherExpenses")}</span>
                      <span className="font-medium text-slate-900">
                        {selectedDay.otherExpenses.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("revenue.fixedCostsProportional")}</span>
                    <span className="font-medium text-slate-900">
                      {selectedDay.fixedCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </span>
                  </div>
                </div>

                {/* Shifts */}
                {selectedDay.shifts.length > 0 && (
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">{t("revenue.laborCostsDay")}</p>
                    <div className="space-y-1.5">
                      {selectedDay.shifts.map((s, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            {s.employee}
                            <span className="text-slate-400 ml-1.5">{s.shift} &middot; {s.hours}h</span>
                          </span>
                          <span className="text-slate-700">&euro;{s.cost}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expenses logged that day */}
                {selectedDay.expenseDetails?.length > 0 && (
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">{t("finances.expensesOverview")}</p>
                    <div className="space-y-1.5">
                      {selectedDay.expenseDetails.map((exp, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            {exp.description || exp.category}
                            <Badge variant="secondary" className={`text-xs font-normal ml-1.5 ${CATEGORY_COLORS[exp.category] || ""}`}>
                              {t(`finances.categories.${exp.category}`, exp.category)}
                            </Badge>
                          </span>
                          <span className="text-slate-700">
                            {exp.amount?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
